import { useState, useCallback } from 'react';
import type { FilterState, InstrumentType, InstrumentStatus } from '@/types';

const DEFAULT_FILTER: FilterState = {
  query: '',
  types: [],
  statuses: [],
  hasMandates: false,
};

export function useFilterState() {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTER);

  const setQuery = useCallback((query: string) => {
    setFilters((f) => ({ ...f, query }));
  }, []);

  const toggleType = useCallback((type: InstrumentType) => {
    setFilters((f) => ({
      ...f,
      types: f.types.includes(type)
        ? f.types.filter((t) => t !== type)
        : [...f.types, type],
    }));
  }, []);

  const toggleStatus = useCallback((status: InstrumentStatus) => {
    setFilters((f) => ({
      ...f,
      statuses: f.statuses.includes(status)
        ? f.statuses.filter((s) => s !== status)
        : [...f.statuses, status],
    }));
  }, []);

  const toggleHasMandates = useCallback(() => {
    setFilters((f) => ({ ...f, hasMandates: !f.hasMandates }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTER);
  }, []);

  const isActive =
    filters.query !== '' ||
    filters.types.length > 0 ||
    filters.statuses.length > 0 ||
    filters.hasMandates;

  return {
    filters,
    isActive,
    setQuery,
    toggleType,
    toggleStatus,
    toggleHasMandates,
    clearFilters,
  };
}
